import React from 'react';
import { Stack, Text } from '@moda/om';
import { PageMeta } from '../../components/PageMeta';
import { Link } from '../../components/Link';
import { useScrollHashReferenceToView } from '../../hooks/useScrollHashReferenceToView';

import './TermsAndConditionsPage.scss';

export const TermsAndConditionsPage = () => {
  useScrollHashReferenceToView();
  return (
    <>
      <PageMeta title="Terms & Conditions" description='"Terms & Conditions' />

      <div className="TermsAndConditionsPage">
        <Stack className="TermsAndConditionsPage__title" space={4}>
          <Text treatment="h2" family="serif">
            Terms &amp; Conditions
          </Text>
        </Stack>

        <Text treatment="eyebrow" className="TermsAndConditionsPage__update">
          Last Updated: July 23, 2025
        </Text>
        <div className="TermsAndConditionsPage__notice">
          <Text treatment="bold1">
            IMPORTANT NOTICE: PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY—THEY AFFECT YOUR
            LEGAL RIGHTS AND OBLIGATIONS, AND INCLUDE WAIVERS OF RIGHTS AND LIMITATIONS OF
            LIABILITY. THEY ALSO REQUIRE DISPUTES BETWEEN YOU AND US TO BE RESOLVED THROUGH BINDING
            INDIVIDUAL ARBITRATION AND TO WAIVE ANY RIGHT TO A JURY TRIAL, CLASS OR COLLECTIVE
            ACTIONS OR PROCEEDINGS, AND ANY OTHER COURT PROCEEDING OF ANY KIND, SUBJECT TO LIMITED
            EXCEPTIONS. UNLESS YOU OPT OUT IN ACCORDANCE WITH THE OPT-OUT PROCEDURES DESCRIBED
            BELOW, YOU WILL BE BOUND BY THESE TERMS AND CONDITIONS. THE FULL TERMS OF THE
            ARBITRATION AGREEMENT ARE BELOW.
          </Text>
        </div>

        <div className="TermsAndConditionsPage__columns">
          <Stack className="TermsAndConditionsPage__column" space={4}>
            <Text treatment="body2" family="serif">
              These terms and conditions ("Terms and Conditions") apply to the purchase and sale of
              products ("Product" or "Products") from or through www.modaoperandi.com (the
              "Website"), our mobile applications ("App"), Moda employees, or at a Moda Operandi
              showroom or event, and also applies to the use of any of our services or features
              provided by or on our behalf (together with the App and Website, "Services"). By
              placing, or authorizing the placing on your behalf, an order for Product(s) or using
              our Services, you agree to be bound by, and you accept, these Terms and Conditions.
              These Terms and Conditions are subject to change by Moda Operandi, Inc. and Moda
              Operandi UK Limited (company number 09075781, registered office at 5 New Street
              Square, London, EC4A 3TW, UK) for customers in Europe (collectively, and together with
              their affiliates and subsidiaries, "Moda", "Moda Operandi", "us", "our", or "we")
              without prior written notice at any time, in our sole discretion. The latest version
              of the Terms and Conditions will be posted on this page, and you should review these
              Terms and Conditions prior to purchasing any Product or using the Services. These
              Terms and Conditions are an integral part of the terms and conditions that apply
              generally to the use of our Services. You should also carefully review our{' '}
              <Link className="TermsAndConditionsPage__link" to="/privacy">
                PRIVACY POLICY
              </Link>{' '}
              before placing an order for Product(s) or using our Services. If you place an order
              for Products or use the Services for or on behalf of a third party, you represent and
              warrant that you have obtained legally valid consent or are otherwise legally
              authorized to place such order and to disclose such third party's personal information
              to Moda Operandi in accordance with with these Terms and Conditions, and for Moda
              Operandi to process such personal information in accordance with our Privacy Policy.
            </Text>
            <Link
              to="#eligibility-orders-cancellation"
              className="TermsAndConditionsPage__section-link"
            >
              <Text treatment="bold1" id="eligibility-orders-cancellation">
                1. Eligibility, Orders, and Cancellation
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              To use the Services you must be, and represent and warrant that you are, of legal age
              (18 years of age or older or otherwise of legal age in your jurisdiction, or, if you
              have parental consent, 13 years of age) and able to agree to these Terms and
              Conditions. If you are agreeing to these Terms and Conditions on behalf of an
              organization or entity, you represent and warrant that you are authorized to agree to
              these Terms and Conditions on that organization or entity's behalf and to bind them to
              these Terms and Conditions (in which case, the references to "you" and "your" in these
              Terms and Conditions, except for in this sentence, refer to that organization or
              entity). If we have previously prohibited you from accessing or using the Services,
              you are not permitted to access or use the Services.
            </Text>
            <Text treatment="body2" family="serif">
              Your receipt of an electronic, or other form of, order confirmation does not signify
              our acceptance of your order, nor does it constitute confirmation of our offer to
              sell. We reserve the right to limit or restrict the availability of Products or
              Services at any time and for any reason. We reserve the right at any time after
              receipt of your order to accept, decline, or limit your order for any reason, whether
              or not you have been charged. If your order has been cancelled or limited by us, the
              applicable amount any payment made pursuant to that order will be refunded to the
              original form of payment. If you have been charged and your order is canceled by us,
              we will promptly issue a refund to your original form of payment in the amount of the
              charge and notify you that your order was canceled. Orders placed using Rewards Points
              (see below) or site credit will be refunded as Rewards Points or site credit,
              respectively, if the order is canceled by us.
            </Text>
            <Text treatment="body1">Trunkshow Orders</Text>
            <Text treatment="body2" family="serif">
              Some of our limited-time presale events allow you to place preorders for Products from
              specific designers or collections that are then made specifically for you
              ("Trunkshow") and fulfilled at a later date when the Product is ready for shipment.
              Until a Trunkshow order period ends, a Trunkshow order may be cancelled for a full
              refund of any deposit to the original method of payment. Once a Trunkshow order period
              ends, we will not be able to make any changes to the size, style, quantity, or type of
              Product ordered. From the time a Trunkshow order period ends until the order is
              delivered to you, you may cancel the order and any prepaid amounts will be refunded to
              you as site credit. Notwithstanding the foregoing, if the Product is Fine Jewelry,
              then the order for that item cannot be canceled after 24 hours from the time of the
              order. Once you receive your order, if you are not satisfied with it, you may return
              it in accordance with our Return Policy set out below, and any such eligible returns
              will be refunded to the original method of payment. Please see Payment Terms below for
              more information on how we process payments and any refunds for Trunkshow orders or
              other preorders.
            </Text>
            <Text treatment="body1">Boutique Orders</Text>
            <Text treatment="body2" family="serif">
              Many of our products are available to you for immediate purchase or for preorder from
              pre-existing or anticipated inventory and are not made for you specifically
              ("Boutique"). Once a Boutique order has been made, we will not be able to cancel the
              order or make changes to the size, style, quantity, or type of Product ordered. Once
              you receive your order, if you are not satisfied with it, you may return it in
              accordance with our Return Policy set out below.
            </Text>
            <Link to="#payment-terms-site-credit" className="TermsAndConditionsPage__section-link">
              <Text id="payment-terms-site-credit" treatment="bold1">
                2. Payment Terms &amp; Site Credit
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Terms of payment are within our sole discretion and, unless otherwise agreed by us in
              writing, shall be as follows:
            </Text>
            <Text treatment="body2" family="serif">
              All orders are subject to verification for security reasons, and we may contact you to
              verify an order. If you are contacted for verification, you will have 48 hours to
              complete the verification process. If you do not respond after such time, you will be
              notified and the order will be canceled and refunded in the original payment method.
            </Text>
            <Text treatment="body2" family="serif">
              Charges for shipping and handling will be made in accordance with our then-current
              shipping policies, as described below. Payments made on debit/credit cards in currency
              other than US Dollars will be calculated using currency conversion on{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://www.xe.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.xe.com
              </a>{' '}
              on the day of the transaction. You agree that Moda is not responsible for any credit
              shortages due to currency conversion rates.
            </Text>
            <Text treatment="body2" family="serif">
              If you preorder Products, you will be required to pay either the full purchase price
              or a percentage of the amount due as an initial payment at the time you place your
              order, exclusive of shipping fees and taxes. In the case of an initial payment, we
              will charge you the remaining amount due, plus shipping fees, and any taxes or duties
              owed at such time as the Product is ready for shipment. We will first apply any
              available site credit in your account to the balance owed, and if you do not have any
              site credit or your site credit is not enough to cover the amount owed, we may charge
              the remaining balance to the same method of payment as used for the initial payment or
              to any active payment methods, including credit card(s), on your account at the time
              the payment is due. If your payment information has changed, please contact{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                CARE@MODAOPERANDI.COM
              </a>{' '}
              with your updated payment information. If the remaining payment cannot be processed,
              your order will be cancelled, and (i) if the item is a Trunkshow item, you will be
              issued a site credit for any amount that you have paid for that Product, and (ii) if
              the item is a Boutique pre-order item, you will be issued a refund to the original
              method of payment.
            </Text>
            <Text treatment="body2" family="serif">
              Please be aware that because of the "pre-sale" nature of such transactions, you should
              expect a wait period of approximately four to six (4 to 6) months between the date you
              place your order and the date that the Product is ready for shipment. The estimated
              delivery window will be noted on the Product page of the respective Trunkshow or
              preorder Product page.
            </Text>
            <Text treatment="body2" family="serif">
              If you order Products available for immediate shipping from our Boutique, you will be
              required to pay the full amount due, plus shipping fees and any taxes and/or duties
              associated with the Product at the time you place your order.
            </Text>
            <div>
              <Text treatment="body1" className="TermsAndConditionsPage__site-credit">
                Site Credit.
              </Text>{' '}
              <Text treatment="body2" family="serif">
                Site credit may only be used for purchases on ModaOperandi.com, is not transferable,
                cannot be redeemed or exchanged for cash. Any eligible returns, cancellations, or
                refunds applicable to a purchase using site credit will be credited back in the form
                of site credit. Site credit automatically expires three (3) years from the date of
                issuance.
              </Text>
            </div>
            <Link to="#changes-products-pricing" className="TermsAndConditionsPage__section-link">
              <Text id="changes-products-pricing" treatment="bold1">
                3. Changes in Products and Pricing
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              We are constantly updating and revising our offerings of Products and Services, and we
              may discontinue Products and Services at any time without notice. All pricing for the
              Products is subject to change. For all of our prices and Products and Services, we
              reserve the right to make adjustments due to changing market conditions, Product
              discontinuation, manufacturer price changes, errors in advertisements, and other
              extenuating circumstances.
            </Text>
            <Text treatment="body2" family="serif">
              We attempt to be as accurate as possible with Product descriptions and prices.
              However, we do not warrant that Product descriptions or prices are accurate. In the
              event that we discover an error in the pricing or description of a Product, we shall
              have the right to refuse or cancel any orders placed for the Product. We shall have
              the right to refuse or cancel any such orders whether or not the order has been
              confirmed and your credit or debit card has been charged.
            </Text>
            <Text treatment="body2" family="serif">
              If you order a Trunkshow Product that was incorrectly priced, you will be contacted by
              a Care representative and given the option to purchase the Product at the correct
              price, or to cancel your order and receive a refund for the amount that you have paid
              as soon as possible, in accordance with our refund policy below. If you order a
              Boutique Product that was incorrectly priced, your order will be canceled and we shall
              issue a refund as soon as possible, in accordance with our refund policy below.
            </Text>
            <Link to="#shipping-tax-duties" className="TermsAndConditionsPage__section-link">
              <Text id="shipping-tax-duties" treatment="bold1">
                4. Shipping Fees, Taxes, and International Duties
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Product prices displayed through the Services include estimated taxes (including value
              added tax ("VAT") and import taxes) and duties.
            </Text>
            <Text treatment="body2" family="serif">
              For orders shipping within the US, sales tax will be charged at checkout on orders
              shipping to states where sales tax is required to be collected by law for online
              purchases. The list of those states may change from time to time. For a list of states
              for which we collect sales tax, please see our{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://help.modaoperandi.com/hc/en-us/articles/211521823-Taxes-Duties"
              >
                FAQs
              </a>
              . For Trunkshow Products, this tax will be charged at such time as the Product is
              ready for shipment. For all other Products, this tax will be charged at the time you
              place your order for the Product. No import duties will be applied to orders shipped
              within the United States.
            </Text>
            <Text treatment="body2" family="serif">
              For international customers, applicable local taxes (including such taxes as VAT),
              import taxes, and duties will be determined, displayed and charged at checkout (unless
              you are shipping to destinations on a DDU (Delivery Duty Unpaid) basis, as discussed
              further, below). All other non-customs-related local taxes (other than VAT) are the
              customer’s responsibility, and Moda Operandi is not responsible for reporting or
              paying additional local taxes to states, such as any use tax. Any taxes and duties are
              calculated on the total merchandise value, after discounts or promotional codes and/or
              promotional site credits which may be redeemed at checkout ("Promotions") have been
              applied.
            </Text>
            <Text treatment="body2" family="serif">
              When shipping to countries outside of the US, most destinations are delivered on a DDP
              (Delivery Duty Paid) basis, which means relevant import taxes and duties are
              calculated at checkout and processed by Moda Operandi once your order ships. If you
              are shipping on a DDU (Delivery Duty Unpaid) basis, import taxes and duties will not
              be paid through Moda Operandi. Instead, the shipping carrier will invoice you upon
              delivery of your merchandise. If you are shipping outside the United States on a DDU
              (Delivery Duty Unpaid) basis, no taxes or duties will be charged by Moda Operandi.
              This detail will be noted at checkout and on the confirmation email sent once order
              has been placed, and as the recipient, you will be responsible and liable for all
              import duties, customs and local sales taxes levied by the country to which you are
              shipping. Payment of these amounts will be necessary to release your order from
              customs on arrival. For a list of DDP and DDU countries please see our{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://help.modaoperandi.com/hc/en-us/articles/211521823-Taxes-Duties"
              >
                FAQs
              </a>
              .
            </Text>
            <Text treatment="body2" family="serif">
              Orders shipped from our United States warehouses will be sent via FedEx, DHL,
              WorldNet, or another carrier. We ship the majority of our Products worldwide from our
              warehouses in the United States. For Trunkshow and preorder Products, all relevant
              shipping fees, local taxes, import taxes, and duties will be charged at such time as
              the Product is ready for shipment. For all other Products, such shipping fees, taxes,
              and duties will be charged at the time you place your order for the Product or at the
              time of shipment. The costs of shipping and any import taxes, and duties paid are
              non-refundable.
            </Text>
            <Link to="#shipping-policy" className="TermsAndConditionsPage__section-link">
              <Text id="shipping-policy" treatment="bold1">
                5. Shipping Policy
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              We offer a variety of shipping options to meet your shipping needs. Please check the
              shipping options at checkout for specific delivery options and fees. No C.O.D. orders
              can be accepted. For more information, please refer to our{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://help.modaoperandi.com/hc/en-us/articles/211521843-Shipping-Times-Costs"
              >
                Shipping FAQ
              </a>
              .
            </Text>
            <Text treatment="body2" family="serif">
              Please note that the posted shipping time frame, if provided, may vary from item to
              item. The posted shipping time frame is contingent upon credit card approval and may
              be delayed should we experience difficulties in obtaining authorization.
            </Text>
            <Text treatment="body2" family="serif">
              These shipping terms, including the terms set forth in our Shipping Guidelines, are
              accepted by you by placing an order with us.
            </Text>
            <Text treatment="body2" family="serif">
              Except as determined otherwise by Moda Operandi in our sole discretion, orders are
              dispatched with ‘No Signature Required’ and may be left at a residence deemed safe by
              the delivery company. Moda Operandi is not responsible for any loss or liability or
              damage that may result from leaving your package at the shipping address without a
              signature.
            </Text>
            <Text treatment="body2" family="serif">
              Moda Operandi is also not responsible for any international shipping delays caused by
              the customs clearance process.
            </Text>
            <Link to="#intellectual-property" className="TermsAndConditionsPage__section-link">
              <Text id="intellectual-property" treatment="bold1">
                6. Intellectual Property and Content
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              All copyright, designs, trademarks and all other intellectual property visible through
              the Services, including Moda Operandi Software and all HTML and other code contained
              on our Website and App, is the sole property of Moda Operandi or their respective
              owners. You are permitted to use the content on the Services only as expressly
              authorized by Moda Operandi and/or its third party licensors. Any reproduction or
              redistribution of the above listed Content without permission is prohibited and may
              result in civil and criminal penalties, where "Content" is defined as data files,
              graphics, text, photographs, drawings, logos, images, sounds, music, video or audio
              files on the Website.
            </Text>
            <Text treatment="body2" family="serif">
              You may not modify, copy, distribute, transmit, display, perform, reproduce, publish,
              license, commercially exploit, create derivative works from, transfer, or sell any
              Content, software, products, or services contained within the Services. You may not
              use the Services, or any of its Content, to further any commercial purpose, including
              any advertising or advertising revenue generation activity on your own site(s).
            </Text>
            <Link to="#price-adjustment" className="TermsAndConditionsPage__section-link">
              <Text id="price-adjustment" treatment="bold1">
                7. Price Adjustment Policy
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Moda Operandi will honor price adjustments on Products purchased within a 7-day period
              prior to the start of a sale. Please note that the Product has to still be available
              on the site in the same size/style as originally purchased. This price adjustment
              policy is available for price adjustments at the initial markdown only; we do not
              offer price adjustments with subsequent markdowns or changes in prices, if any. Moda
              Operandi does not price match any sale or discount prices with any other retailers,
              including affiliates.
            </Text>
            <Text treatment="body2" family="serif">
              All approved price adjustments will be issued as a refund to the original method of
              payment and the Product will be a Final Sale. If you wish to request a price
              adjustment in accordance with this policy, please contact{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                CARE@MODAOPERANDI.COM
              </a>
              .
            </Text>
            <Link to="#limitation-of-liability" className="TermsAndConditionsPage__section-link">
              <Text id="limitation-of-liability" treatment="bold1">
                8. Limitation of Liability; No Warranties
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Any conditions, rights, warranties or guarantees implied by law into these Terms and
              Conditions or with respect to your purchase of Products are expressly excluded to the
              extent permitted by law. In no event shall Moda Operandi, its affiliates, and its and
              their respective officers, directors, managers, employees, and other representatives
              (collectively, the “Moda Parties”), under any circumstances, have any obligations or
              liabilities to you or any other person (i) for loss of profits, for loss of business
              or use, or for incidental, punitive, special, or consequential damages, whether based
              on contract, tort (including negligence), products liability, or any other theory or
              form of action, even if we have been advised of the possibility thereof, (ii) arising
              out of or in connection with the sale, delivery, or use of or inability to use the
              Products or Services, (iii) as publisher of information, (iv) as a reseller of any
              products or services, (v) for any incorrect or inaccurate information, or (vi) for
              statements or conduct of any third party on or through the Services. Subject to the
              above exclusion of liability and to the maximum extent permitted by law, our sole and
              entire maximum liability (and the liability of our suppliers) for any reason, and your
              sole and exclusive remedy for any cause whatsoever, shall be limited to the actual
              amount paid by you for the Products you have ordered through our Services. The
              limitations of liability set forth herein are fundamental elements of the basis of the
              bargain between Moda and you.
            </Text>
            <Text treatment="body2" family="serif">
              You expressly understand and agree that:
            </Text>
            <Text treatment="body2" family="serif">
              <ul className="TermsAndConditionsPage__list">
                <li>
                  The Services and all materials and content contained therein are provided on an
                  “as is” and “as available” basis, and your use of the Services is at your sole
                  risk. Except as otherwise expressly provided in these Terms and Conditions, the
                  Moda Parties expressly disclaim all warranties of any kind, whether express or
                  implied, including, but not limited to the implied warranties of merchantability,
                  fitness for a particular purpose, and non-infringement. Without limiting the
                  generality of the foregoing, the Moda Parties make no warranty that: (i) the
                  Services will meet your requirements; (ii) the Services will be uninterrupted,
                  timely, secure, or error-free; (iii) information that may available or contained
                  in the Services will be accurate or reliable; and (iv) the quality of any
                  products, services, information, or other material obtained through the Services
                  will meet your expectations or needs.
                </li>
                <li>
                  All Products purchased from Moda are provided “as-is”, and subject only to any
                  applicable warranties of their respective manufacturers, distributors, and/or
                  suppliers, if any. To the fullest extent permitted by applicable law, the Moda
                  Parties expressly disclaim all warranties of any kind, either express or implied,
                  including, any implied warranties, with respect to the Products listed or
                  purchased on or through the Services. Without limiting the generality of the
                  foregoing, the Moda parties expressly disclaim all liability for product defect,
                  fault, or failure, claims that are due to normal wear, misuse, abuse,
                  modification, and non-compliance with any directions.
                </li>
              </ul>
            </Text>
            <Text treatment="body2" family="serif">
              Some jurisdictions do not allow the disclaimer of certain warranties limitation or
              exclusion of liability for certain damages. Accordingly, some of the above disclaimers
              and limitations may not apply to you to the extent such jurisdiction’s law is
              applicable to your purchase of Products. For UK and European Union residents, the
              above disclaimers products do not affect your legal rights where Products are faulty
              or otherwise not in conformance with the contract between us and you.
            </Text>
            <Link to="#return-policy" className="TermsAndConditionsPage__section-link">
              <Text id="return-policy" treatment="bold1">
                9. Return Policy (Returns and Refunds)
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Moda Operandi accepts returns in accordance with this Return Policy. Subject to
              certain conditions and exceptions explained below in this Return Policy, and without
              prejudice to your rights under applicable law, and unless otherwise expressly
              indicated on a Product's detail page at the time of purchase, we are pleased to accept
              timely returns of certain new and unused Products, as follows:
            </Text>
            <Text treatment="body2" family="serif">
              <ul className="TermsAndConditionsPage__list">
                <li>
                  <span className="TermsAndConditionsPage__italic">Full Price</span>: Products
                  purchased at full price are eligible for return for a full refund within 28 days
                  from the date that the Product was delivered to you.
                </li>
                <li>
                  <span className="TermsAndConditionsPage__italic">Markdowns</span>: Products
                  purchased at a discount (other than with a Promotion) or on markdown are eligible
                  for return for site credit within 14 days from the date that the Product was
                  delivered to you.
                </li>
                <li>
                  Fine Jewelry: Fine Jewelry Products are eligible for return for a full refund
                  within 14 days from the date the Product was delivered to you.
                </li>
                <li>
                  Timepieces: Timepiece Products are eligible for return for a full refund within 14
                  days from the date the Product was delivered to you.
                </li>
                <li>
                  <span className="TermsAndConditionsPage__italic">Final Sale</span>: Products
                  purchased on or designated as final sale (as further described below) are not
                  eligible for return or a refund.
                </li>
              </ul>
            </Text>
            <Text treatment="body2" family="serif">
              Pre-orders placed across multiple cards will be refunded to the same cards used,
              regardless of the cards being removed from the site, cards expiring, or updated card
              information.
            </Text>
            <Text treatment="body2" family="serif">
              Your Products must be returned in new, unused, unopened (if the Product is sealed) and
              unworn condition (with any protective or hygienic strips as originally attached), in
              the original packaging, including any boxes, hangers, garment bags, protective
              coverings in place, and other signature packaging materials, including Moda Operandi
              and designer stickers, labels, and hanging tags. Other designer packaging such as
              belts, dust bags, authenticity cards, and leather tags must all be included where
              provided. If your order did not include the proper packaging please email{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                CARE@MODAOPERANDI.COM
              </a>
              . Moda Operandi reserves the right, at its sole discretion, to determine if returned
              Products are eligible for a return or refund in accordance with this Return Policy.
            </Text>
            <Text treatment="body2" family="serif">
              Any returned Products which are worn, used, washed, damaged, do not include the
              packaging materials, soiled, or altered may not be accepted and refunds or credits
              will not be issued for such returns. If you are resident in the UK or European Union
              exercising your right to cancel any time up to 14 days from the day after the date
              after delivery (the "cooling off period"), we may make deductions from any refund due
              to your use of, or damage to, the Products ("Deductions"). Refunds or credits will not
              be processed until the items are returned and inspected by Moda. For orders within the
              United States, any shipping or gift wrapping charges are non-refundable. For
              international orders, the costs of gift wrapping, shipping, and any import taxes and
              duties paid are non-refundable. If you are resident in the UK or European Union
              exercising your right to cancel within the cooling off period, we will repay all
              amounts you pay except for any Deductions and any additional costs you pay for
              non-standard shipping.
            </Text>
          </Stack>

          <Stack className="TermsAndConditionsPage__column" space={4}>
            <Text treatment="body2" family="serif">
              Moda Operandi monitors the number and condition of Products returned and orders
              cancelled by customers. Repeated or excessive returns or cancellations, as determined
              by us in our sole discretion, or violations of our Terms and Conditions will be
              flagged and may, at our discretion, lead to restrictions on access to and/or use of
              the Services, additional terms and conditions applicable to you and your future orders
              with us (and such restrictions and/or additional terms and conditions shall supersede
              and govern in the event of a conflict with these Terms and Conditions), future orders
              and/or returns being refused, and/or the closure of your Moda Operandi account and
              accounts associated with you, in each case, as determined by us in our sole
              discretion.
            </Text>
            <Text treatment="body2" family="serif">
              For Australian customers, we only accept refunds as set out in this clause or as
              required under Australian Consumer Law or other laws that cannot be lawfully excluded.
            </Text>
            <Text treatment="body1">Shoes</Text>
            <Text treatment="body2" family="serif">
              Shoes must be tried on over a carpeted surface to prevent any scratches to the soles.
              All shoes must be returned in their original shoe box. Shoes returned without the
              original box, a damaged box, or marked soles may not be accepted and will be returned
              to you, and if you are resident in the UK or European Union exercising your right to
              cancel within the cooling off period, then we may make Deductions.
            </Text>
            <Text treatment="body1">Late Returns</Text>
            <Text treatment="body2" family="serif">
              At the discretion of Moda Operandi, any Product returned outside of the deadline for
              returns may be rejected or only accepted for site credit.
            </Text>
            <Text treatment="body1">Product Colors</Text>
            <Text treatment="body2" family="serif">
              We have endeavored to ensure the Product colors are displayed as accurately as
              possible. Moda cannot guarantee your computer’s monitor will accurately display the
              true color. If you have any questions about the color of an item please contact{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                CARE@MODAOPERANDI.COM
              </a>{' '}
              prior to placing your order.
            </Text>
            <Text treatment="body1">Final Sale—Items Not Eligible for Return or Refund</Text>
            <Text treatment="body2" family="serif">
              All Products that are (i) considered Final Sale as listed below or on our{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://help.modaoperandi.com/hc/en-us/articles/360000144503-Shopping-Sale"
              >
                Shopping Sale FAQ page
              </a>
              , or (ii) otherwise indicated as "final sale", are not eligible to be returned,
              exchanged, or refunded. Certain exceptions may apply to customers in jurisdictions
              where we may be required to accept returns, exchanges, or refunds by law, such as for
              customers in the UK and the European Union—please see our{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://help.modaoperandi.com/hc/en-us/articles/360000144503-Shopping-Sale"
              >
                Shopping Sale FAQ page
              </a>{' '}
              for more information.
            </Text>
            <Text treatment="body2" family="serif">
              <ul className="TermsAndConditionsPage__list">
                <li>Customized, personalized, custom, or made to order items</li>
                <li>Vintage items</li>
                <li>Hosiery / lingerie</li>
                <li>Exotic leathers</li>
                <li>Wedding dresses</li>
                <li>Special Order Items</li>
                <li>Items indicated as "final sale"</li>
              </ul>
            </Text>
            <Text treatment="body2" family="serif">
              If you return or send to Moda any Product that is not eligible for return or refund in
              accordance with these Terms and Conditions (including, but not limited to items
              considered Final Sale), any Product that is not listed on the return authorization
              from Moda in respect of the respective shipment from you to us, and/or any product
              that you did not purchase from Moda, we may, without notice to you, retain and dispose
              of such Product(s) and/or product(s) at our discretion, and refunds or credits will
              not be issued for such Product(s) and/or product(s).
            </Text>
            <Text treatment="body1">UK and EU residents</Text>
            <Text treatment="body2" family="serif">
              If you are resident in the UK or European Union, you may cancel an order at any time
              before your order is delivered and up to 14 days afterwards, beginning on the day
              after your order is delivered to you. You must also return the Products to us within
              14 days after the day of notifying us of the cancellation, in the same condition in
              which you receive them (which does not interfere with your right to take any
              reasonable steps to examine the Products and make sure they conform to your order).
              You are responsible for the costs to return to us Products that are indicated as
              "Final Sale".
            </Text>
            <Text treatment="body2" family="serif">
              You will not have such right to cancel an order for the supply of any Products which
              are exempted by law from the right of withdrawal, including, but not limited to, the
              following Products:
            </Text>
            <Text treatment="body2" family="serif">
              <ul className="TermsAndConditionsPage__list">
                <li>Customized or personalized Products</li>
                <li>Swimwear or lingerie with the protective or hygienic strip removed</li>
                <li>
                  Cosmetic, skincare, or supplements, fragrance Products which were unsealed after
                  delivery
                </li>
              </ul>
            </Text>
            <Text treatment="body2" family="serif">
              unless such Products were faulty when delivered to you.
            </Text>
            <Text treatment="body2" family="serif">
              You may notify us of such cancellation by using our cancellation form available{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="/dist/public/model_cancellation_form.pdf"
                aria-label="Model Cancellation Form PDF"
              >
                here
              </a>{' '}
              or contacting us at{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                CARE@MODAOPERANDI.COM
              </a>
              .
            </Text>
            <Text treatment="body1">Return Process</Text>
            <Text treatment="body2" family="serif">
              If you would like to return an eligible Product, please use the applicable return
              label as provided by Moda Operandi. To request a return shipping label:
            </Text>
            <Text treatment="body2" family="serif">
              <ul className="TermsAndConditionsPage__list TermsAndConditionsPage__indent">
                <li>
                  If you have a Moda Operandi account, sign in and locate the item/order under
                  "ORDER HISTORY". Click "REQUEST RETURN" next to the Product(s) you wish to return,
                  and follow any instructions. A pop-up page will appear with the prepaid shipping
                  label, instructions, and international commercial invoices (where applicable)
                </li>
                <li>
                  If you checked out as a guest, you can request a return online at{' '}
                  <a
                    className="TermsAndConditionsPage__link"
                    href="https://www.modaoperandi.com/request-return"
                  >
                    https://www.modaoperandi.com/request-return
                  </a>{' '}
                  or by contacting Care.
                </li>
              </ul>
            </Text>
            <Text treatment="body2" family="serif">
              You will also receive a "Return Requested" email confirmation with a copy of the
              label, instructions, and international commercial invoices (where applicable)
              attached.
            </Text>
            <Text treatment="body2" family="serif">
              Simply affix the return label to the outside of the box. International orders must
              include 3 copies of the commercial invoices of the Products being returned. This is to
              prevent any delays with clearing international customs.
            </Text>
            <Text treatment="body2" family="serif">
              Without prejudice to your rights under applicable law, please note that our free
              return shipping does not apply to items returned to us from countries other than the
              country to where they were originally shipped, or to returns that do not use the
              appropriate return shipping label that we provide.
            </Text>
            <Text treatment="body2" family="serif">
              Please note that Moda Operandi may not accept liability for any Products or other
              goods returned to us outside our own return labels.
            </Text>
            <Text treatment="body2" family="serif">
              Refunds are generally processed within 7-10 business days from when the returned item
              is received at our warehouse, subject to any shorter refund period which is mandated
              by applicable law.
            </Text>
            <Link to="#contests-and-promotions" className="TermsAndConditionsPage__section-link">
              <Text id="contests-and-promotions" treatment="bold1">
                10. Contests and Promotions
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Promotions are non-transferrable and may not be exchanged or redeemed for cash or
              credit or after any applicable expiration date, and are eligible to be used only by
              the intended recipient and for eligible purchases. Promotions may not be applied to
              prior orders, and may only be used once. Moda Operandi reserves the right to, at any
              time, modify, revoke, or cancel a Promotion or offer of a Promotion, and/or to reject
              redemption of a Promotion, including, but not limited to, when the total value of the
              Promotion exceeds a certain value (for example, the price of the applicable
              Product(s)).
            </Text>
            <Text treatment="body2" family="serif">
              Unless we expressly provide otherwise, Promotions are only eligible for orders of
              full-priced Products and are limited to one per transaction. Minimum purchase amounts
              may be required and will be communicated on any correspondence. Moda Operandi shall
              not be liable to any customer for any financial loss arising out of the refusal,
              cancellation or withdrawal of a Promotion or any failure or inability of a customer to
              use a promotional code for any reason. Certain brands or Products may be excluded from
              Promotions, as determined by us in our sole discretion. In the event of a cancellation
              or refund of an order to which a Promotion is applied, the value of a Promotion will
              not be refunded.
            </Text>
            <Text treatment="body2" family="serif">
              Promotional codes do not apply to the costs of shipping, or to sales tax, VAT,
              regional taxes, duties, import taxes, or gift wrapping charges.
            </Text>
            <Text treatment="body2" family="serif">
              Moda may offer you the opportunity to participate in a contest or giveaway
              (“Contest”). Such opportunities are not transferable or redeemable for cash or site
              credit, and may only be used by the intended recipient. Moda reserves the right to
              modify, revoke, or cancel a Contest at any time and for any reason. If the Contest is
              for a product or service provided by or with a third party, if you participate in the
              Contest (for example, by using an applicable promotional code), you consent to Moda
              sharing your name and contact information with such third party in connection with the
              Contest. Contests may be subject to eligibility requirements, such as a minimum
              purchase, and exclusions may apply, in each case, as determined by us in our sole
              discretion. Additional terms may apply.
            </Text>
            <Link to="#rewards" className="TermsAndConditionsPage__section-link">
              <Text id="rewards" treatment="bold1">
                11. Moda Rewards
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Members of Moda Rewards Program may earn Rewards Points and receive other exclusive
              perks. Moda Rewards Point balances are displayed in your account center. Your account
              must be in good standing to receive, and use, points. Please see our{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://help.modaoperandi.com/hc/en-us"
              >
                FAQs
              </a>{' '}
              for additional terms and information about earning and using Moda Rewards Points.
            </Text>
            <Text treatment="body2" family="serif">
              Merchandise returns and other financial adjustments will be deducted from your total
              Moda Rewards Program point balance, at the sole discretion of Moda Operandi. Points
              are awarded on dollars spent on the Product, not including duties, tariffs, taxes, and
              shipping costs.
            </Text>
            <Text treatment="body2" family="serif">
              Moda Rewards are not transferrable and Rewards Points may only be utilized for
              purchases made at{' '}
              <a className="TermsAndConditionsPage__link" href="/">
                WWW.MODAOPERANDI.COM
              </a>{' '}
              or on the Moda app. Moda Rewards are not awarded to corporate accounts. Rewards Points
              expire 365 days from the date of issuance.
            </Text>
            <Text treatment="body2" family="serif">
              Moda Operandi may terminate this rewards program at any time, with or without notice,
              which may result in forfeiture of any unused but accumulated points. Moda Operandi may
              also limit, delete or change any of the rules, terms, conditions, benefits or
              eligibility for the program, with or without notice. This, too, may result in a loss
              of any unused but accumulated points.
            </Text>
            <Text treatment="body2" family="serif">
              By becoming a Moda Rewards member you agree to be bound by these Terms and Conditions.
            </Text>
            <Text treatment="body2" family="serif">
              For questions regarding your point balance or the Moda Rewards Program please contact{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                CARE@MODAOPERANDI.COM
              </a>{' '}
              or by calling Client Services at{' '}
              <a className="TermsAndConditionsPage__link" href="tel: (800) 707-9898">
                (800) 707-9898
              </a>{' '}
              if you are calling from the United States, or{' '}
              <a className="TermsAndConditionsPage__link" href="tel: +1 (212) 729-0984">
                +1 (212) 729-0984
              </a>{' '}
              if you are calling internationally.
            </Text>
            <Link
              to="#your-content-and-prohibited-issues"
              className="TermsAndConditionsPage__section-link"
            >
              <Text id="your-content-and-prohibited-issues" treatment="bold1">
                12. Your Content and Prohibited Uses
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              Certain Services may permit you to publicly post or display content, and you alone are
              responsible for such content. You are prohibited from using, and agree not to use, the
              Services or its content, or to submit, publish, or post any content to us or in
              connection with the Services: (a) for any unlawful purpose or to solicit others to
              perform or participate in any unlawful or fraudulent conduct; (b) to violate any
              international, federal, provincial or state regulations, rules, laws, or local
              ordinances; (c) to infringe upon or violate our rights or the rights of others
              (including, but not limited to, intellectual property rights); (d) to defraud, harass,
              abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate based on
              gender, sexual orientation, religion, ethnicity, race, age, national origin,
              disability or any other protected attribute; (e) to submit false or misleading
              information; (f) to upload or transmit viruses or any other type of malicious code
              that will or may be used in any way that will affect the functionality or operation of
              the Services or of any related website, other websites, or the Internet; (g) to
              collect, track, or publish the personal information of others; (h) to spam, phish,
              pharm, pretext, spider, crawl, or scrape; (i) for any obscene or immoral purpose; or
              (j) to interfere with or circumvent the security features of the Services. We reserve
              the right to monitor, edit, delete, or use any such information as we deem
              appropriate, including, but not limited to, to protect our Services, the rights and
              property of us, you, and others, and as may be required by or appropriate pursuant to
              law or requirement of other governmental authority.
            </Text>
            <Text treatment="body2" family="serif">
              We reserve the right to suspend or terminate your account(s) and any orders (and any
              other orders or account(s) connected with you) and your use of and/or access to the
              Services if you, as determined by us in our sole discretion: (i) violate these Terms
              and Conditions or our Privacy Policy, (ii) engage in defamatory, fraudulent, abusive,
              or illegal or other misconduct in connection with our Products or Services, (iii)
              engage in behavior that abuses, harasses, insults, or intimidates another customer,
              our team members, vendors, suppliers, agents, or representatives, or (iv) engage in
              fraud, abuse, misuse, or gaming in connection with our Products, Services, or
              Promotions, Contests, or Rewards Points in any way, or that you intend to do so (for
              example, if you create more than one account to obtain a Promotion that is intended
              for new customers).
            </Text>
            <Text treatment="body2" family="serif">
              You are not authorized to, and agree not to, download or copy any images, works, or
              other content displayed or performed on or through the Services, including
              modaoperandi.com and our mobile applications.
            </Text>
            <Link to="#exceptional-products" className="TermsAndConditionsPage__section-link">
              <Text id="exceptional-products" treatment="bold1">
                13. Exceptional Products
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              From time to time, we may offer for sale certain "Exceptional Products", which are
              Products or experiences in a limited-time sale event that allows you to place orders
              or inquiries from specific designers or vendors that are specially curated by us, have
              limited quantities, and designed for an exceptional experience, and which the
              applicable vendor ("EP Vendor") will supply, deliver, provide, fulfill, and/or perform
              ("EP Fulfillment") at one or more later times. Exceptional Products are also subject
              to, and by placing an order or inquiry for an Exceptional Product you agree to, the
              following additional terms and conditions:
            </Text>
            <Text treatment="body2" family="serif">
              <ul className="TermsAndConditionsPage__list">
                <li>Exceptional Products are available on a first-come, first-served basis.</li>
                <li>
                  If you purchase an Exceptional Product directly from us:
                  <ul className="TermsAndConditionsPage__list TermsAndConditionsPage__indent">
                    <li>
                      All sales of Exceptional Products are final and non-refundable (subject to
                      applicable law and EP Vendor availability), and resales are prohibited, but
                      you may gift your purchase of an Exceptional Product to another person, so
                      long as you provide us notice of their contact information, with their
                      permission, prior to finalizing your order. Any sales tax, duties, import
                      taxes, or similar charges, will be determined based on the recipient’s address
                      and collected at the time of any balance payment.
                    </li>
                    <li>
                      If the purchase price for an Exceptional Product is not collected in full at
                      the time of your order, to finalize your purchase (and notwithstanding
                      anything to the contrary in any order/checkout confirmation page or
                      communication), you must pay any balance (including any applicable duties,
                      taxes, VAT, or similar charges) in full within 72 hours, unless a greater time
                      period is indicated in the Exceptional Product detail page. We will promptly
                      reach out to you at the email address or phone number in your order to
                      finalize your order and process any balance payment. If you do not pay the
                      balance in full within the applicable time period, we reserve the right to
                      cancel your order, return any deposit, and offer the Exceptional Product for
                      sale publicly.
                    </li>
                    <li>
                      Pursuant to your purchase of an Exceptional Product, we will provide the
                      applicable EP Vendor with your (or the designated recipient's) name and
                      contact information so that the EP Vendor may perform the EP Fulfillment. You
                      give us permission to share such information (or the contact information of
                      your designated gift recipient, and you confirm to us that you have their
                      permission for us to share such information) with the EP Vendor for the
                      purposes of EP Fulfillment, and to cooperate and assist in such EP Fulfillment
                      as we deem necessary or appropriate.
                    </li>
                    <li>
                      As between you and us, the EP Vendor is solely responsible for EP Fulfillment,
                      and we are only responsible to collect from you payment for the Exceptional
                      Product and to use our commercially reasonable efforts to confirm your order
                      with the EP Vendor. If the EP Vendor does not confirm your order for any
                      reason, we will issue you a refund in full.
                    </li>
                  </ul>
                </li>
                <li>
                  If you purchase of an Exceptional Product from the EP Vendor:
                  <ul className="TermsAndConditionsPage__list TermsAndConditionsPage__indent">
                    <li>
                      Upon your inquiry and confirmation to us of your intent to purchase the
                      Exceptional Product, we will provide the applicable EP Vendor with your (or
                      your designated recipient’s) name and contact information so that the EP
                      Vendor may finalize the order as it deems necessary or appropriate, collect
                      payment, and perform the EP Fulfillment. You give us permission to share such
                      information (or the contact information of your designated gift recipient, and
                      you confirm to us that you have their permission for us to share such
                      information) with the EP Vendor for the purposes of negotiating and finalizing
                      your order, collecting payment, and EP Fulfillment. For clarity, such orders
                      are not final or confirmed until accepted as such by the EP Vendor pursuant to
                      its applicable processes, terms, and conditions.
                    </li>
                    <li>
                      As between you and us, the EP Vendor is solely responsible for EP Fulfillment,
                      and to collect from you payment for the Exceptional Product and any applicable
                      sales tax, VAT, duties, import taxes, or other government charges and fees,
                      but we may cooperate and assist you and/or the EP Vendor in in such EP
                      Fulfillment as we deem necessary or appropriate.
                    </li>
                  </ul>
                </li>
                <li>
                  EP Fulfillment will take place in the time and manner as specified in the
                  Exceptional Product detail page and in the EP Vendor's reasonable discretion, and
                  as may otherwise be agreed by you or us with the EP Vendor. If EP Fulfillment is
                  delayed or canceled in connection with events outside of the EP Vendor's
                  reasonable control, the EP Vendor may, in its discretion, issue a refund, or
                  reschedule, defer, or reasonably modify the EP Fulfillment.
                </li>
                <li>
                  The price of an Exceptional Product covers the cost of the Exceptional Product
                  only. Unless expressly indicated otherwise in the Exceptional Product detail page,
                  the price does not include sales tax, VAT, import taxes, duties, or any travel,
                  lodging, meals, services, documentation, insurance, registrations, modifications,
                  customization, or government fees or charges. If the EP Vendor offers or permits
                  you to make customizations or modifications to or in connection with the
                  Exceptional Product or EP Fulfillment, any applicable costs or expenses (and any
                  related taxes or charges) are your sole responsibility, and you agree to pay such
                  amounts upon request by us or the EP Vendor.
                </li>
                <li>
                  We do not provide any warranty or guarantee with respect to an Exceptional Product
                  or EP Fulfillment. You agree that we are not liable for, and you expressly agree
                  to waive and release all claims, now known or hereafter known, against us and our
                  affiliates and our and their employees, owners, shareholders, directors, officers,
                  agents, and representatives (collectively, "Moda Parties"), and to hold us and the
                  Moda Parties harmless from and against any claims, charges, liabilities, losses,
                  injury, damages, and costs, in each case, in connection with your purchase of an
                  Exceptional Product and EP Fulfillment.
                </li>
                <li>
                  You are responsible to comply with all applicable laws, rules, and regulations in
                  connection with the Exceptional Product and your participation in the EP
                  Fulfillment. We do not warrant that Exceptional Product descriptions are complete
                  or accurate, and may be subject to reasonable changes at EP Vendor’s discretion.
                  EP Fulfillment may be subject to additional terms and conditions by the EP Vendor,
                  and you are responsible to review, assent to, and comply with any such terms.
                </li>
              </ul>
            </Text>
            <Link to="#sms-terms" className="TermsAndConditionsPage__section-link">
              <Text id="sms-terms" treatment="bold1">
                14. SMS Terms
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              We may seek to send messages to you on your wireless device ("Text Messaging"). By
              joining a text message program, you agree and give us your prior express written
              consent to receive Text Messaging, including recurring promotional and personalized
              text (e.g., SMS or MMS) messages from us or our agents to any phone number you provide
              us using automated or non-automated technology (including an automatic telephone
              dialing system), and/or prerecorded messages, even if your number is listed on the
              federal or state "do not call" registries. You are not required to agree to receive
              promotional text messages as a condition to purchase goods or services from us.
              Message and data rates may apply.
            </Text>
            <Text treatment="body2" family="serif">
              You represent that you are the owner or authorized user of the device you use to sign
              up for Text Messaging and that you are authorized to approve the applicable charges.
              You may not consent on behalf of someone else. You affirm that you are of legal age to
              form a binding contract, or have obtained parental or guardian consent to do so before
              using our Text Messaging service.
            </Text>
            <Text treatment="body2" family="serif">
              Through the provision of our Text Messaging service, we and our service provider may
              receive and store data about text messages exchanged, including the date and time of
              the text message, your name, your phone number, and the content of the text message.
              We will use the information we obtain in connection with Text Messaging in accordance
              with these SMS terms, and our{' '}
              <Link className="TermsAndConditionsPage__link" to="/privacy">
                Privacy Policy
              </Link>
              .
            </Text>
            <Text treatment="body2" family="serif">
              We may send you an initial message confirming that we have received your opt-in to
              Text Messaging. After that, you may receive recurring text messages, and the specific
              frequency amount may vary. The number of text messages you receive may depend upon
              factors including how you use our Services and whether you take steps to generate
              additional text messages from us (such as by sending a HELP request). We reserve the
              right to alter the frequency of messages at any time, so as to increase or decrease
              the total number of sent messages. We also reserve the right to change the short code
              or phone number from which messages are sent and we will notify you when we do so.
            </Text>
            <Text treatment="body2" family="serif">
              To opt out of promotional Text Messaging at any time, you must text "STOP," "END,"
              "CANCEL," "UNSUBSCRIBE," or "QUIT" in response to any such text messages. You agree
              that texting "STOP," "END," "CANCEL," "UNSUBSCRIBE," or "QUIT" in response to our text
              message is the only reasonable method of opting out. You also understand and agree
              that any other method of opting out, including, but not limited to, texting words
              other than "STOP," "END," "CANCEL," "UNSUBSCRIBE," or "QUIT" or verbally requesting
              one of our employees to remove you from our list, is not a reasonable means of opting
              out. You acknowledge that our text message platform may not recognize and respond to
              unsubscribe requests that do not include the STOP, END, CANCEL, UNSUBSCRIBE or QUIT
              keyword commands and agree that Moda and our service providers will have no liability
              for failing to honor such requests. We may send you a text confirmation of your
              opt-out. If you unsubscribe from one of our Text Messaging programs, you may continue
              to receive Text Messaging from Moda through any other programs you have joined until
              you separately unsubscribe from those programs. If you opt out of promotional text
              messages, we may still contact you about your transactions with us.
            </Text>
            <Text treatment="body2" family="serif">
              Text the keyword HELP to our shortcode to return customer care contact information. If
              you are experiencing any problems, you can also reach out to us at{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                care@modaoperandi.com
              </a>
              .
            </Text>
            <Text treatment="body2" family="serif">
              By agreeing to these SMS Terms, you also consent to the use of an electronic record to
              document your agreement. You may withdraw your consent to the use of the electronic
              record by emailing us at privacy@modaoperandi.com with “Revoke Electronic Consent” in
              the subject line.
            </Text>
            <Text treatment="body2" family="serif">
              To view and retain a copy of this disclosure or any information regarding your
              enrollment in this program, you will need (i) a device (such as a computer or mobile
              phone) with a web browser and Internet access and (ii) either a printer or storage
              space on such device. For a free paper copy, or to update our records of your contact
              information, email us at{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:privacy@modaoperandi.com">
                privacy@modaoperandi.com
              </a>{' '}
              with contact information and the address for delivery.
            </Text>
            <Text treatment="body2" family="serif">
              Mobile access is required for Text Messaging, and text messages are not available or
              deliverable on some carriers and devices or in some areas. Content may not be
              compatible with all cell phone models, and the available carriers may change at any
              time. Moda and its service providers or the mobile carriers (e.g. T-Mobile) are not
              liable for any delayed or undelivered messages.
            </Text>
          </Stack>

          <Stack className="TermsAndConditionsPage__column" space={4}>
            <Link to="#use-of-products" className="TermsAndConditionsPage__section-link">
              <Text id="use-of-products" treatment="bold1">
                15. Use of Products
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              You should use the Products in accordance with their instructions / directions and
              only for their intended purpose. Please read the Product labels and information before
              use.
            </Text>
            <Link to="#miscellaneous" className="TermsAndConditionsPage__section-link">
              <Text id="miscellaneous" treatment="bold1">
                16. Miscellaneous
              </Text>
            </Link>
            <Text treatment="body2" family="serif">
              These Terms and Conditions constitute the entire agreement and understanding between
              us and you concerning the subject matter hereof. These Terms and Conditions may not be
              altered, supplemented, or amended orally or by the use of any other documents. No
              failure or delay on the part of Moda Operandi in exercising any right, power, or
              remedy hereunder shall operate as a waiver thereof, nor shall any single or partial
              exercise or waiver of any such right, power or remedy preclude any other or further
              exercise thereof or the exercise of any other right, power or remedy. The headings in
              these Terms and Conditions are for convenience only, and shall not limit or otherwise
              affect the meaning or interpretation hereof. You shall indemnify us and hold us
              harmless for any costs, losses, damages, or claims arising in connection with your
              breach or threatened breach of these Terms and Conditions. If you have any questions
              or concerns regarding these Terms and Conditions, please contact{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:care@modaoperandi.com">
                care@modaoperandi.com
              </a>{' '}
              or by calling our Care team at{' '}
              <a className="TermsAndConditionsPage__link" href="tel: (800) 707-9898">
                (800) 707-9898
              </a>{' '}
              if you are calling from the United States, or{' '}
              <a className="TermsAndConditionsPage__link" href="tel: +1 (212) 729-0984">
                +1 (212) 729-0984
              </a>{' '}
              if you are calling internationally. Please note that communications with Care may be
              recorded or monitored.
            </Text>
            <Link to="#dispute" className="TermsAndConditionsPage__section-link">
              <Text id="dispute" treatment="bold1">
                17. Dispute Resolution
              </Text>
            </Link>
            <Text treatment="body1">
              A.{' '}
              <Text className="TermsAndConditionsPage__underline">
                Dispute Resolution for US Residents: Agreement To Arbitrate; Class Action and Jury
                Waiver
              </Text>
            </Text>
            <Text treatment="body2" className="TermsAndConditionsPage__italic">
              Last Updated: July 23, 2025
            </Text>
            <Text treatment="body2" className="TermsAndConditionsPage__italic">
              If you are a resident of the United States, the following terms apply to you.
            </Text>
            <Text treatment="bold1">
              PLEASE READ THIS SECTION (THE “ARBITRATION AGREEMENT”) CAREFULLY. IT AFFECTS YOUR
              LEGAL RIGHTS. IT PROVIDES FOR RESOLUTION OF MOST DISPUTES THROUGH INDIVIDUAL
              ARBITRATION INSTEAD OF COURT TRIALS AND CLASS ACTIONS. THIS SECTION ALSO CONTAINS A
              JURY TRIAL WAIVER AND A WAIVER OF ANY AND ALL RIGHTS TO PROCEED IN CLASS, COLLECTIVE,
              PRIVATE ATTORNEY GENERAL, REPRESENTATIVE, OR CONSOLIDATED ACTION IN ARBITRATION OR
              LITIGATION TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW. UNLESS YOU OPT OUT IN
              ACCORDANCE WITH THE OPT-OUT PROCEDURES DESCRIBED BELOW, YOU WILL BE BOUND BY THIS
              ARBITRATION AGREEMENT.
            </Text>
            <Text treatment="body2" family="serif">
              1. Mandatory Individual Arbitration
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">
                Mandatory Individual Arbitration.
              </span>{' '}
              Any dispute, claim, or controversy between you, on the one hand, and Moda and/or any
              of its affiliates and/or its and their employees, officers, or directors
              (collectively, “Moda Parties”), on the other hand, including, but not limited to,
              those relating to or arising from (i) the Services, Products, or (ii) these Terms and
              Conditions (including this Arbitration Agreement), or any other contract, arrangement,
              Moda policy or FAQ, agreement, or terms entered into by you and Moda that are governed
              by, or otherwise incorporate or reference, or are incorporated by reference in, these
              Terms and Conditions (“Other Terms”), including, without limitation, those relating to
              the formation, breach, termination, enforcement, interpretation, validity, scope, or
              applicability of the Terms and Conditions (including the Arbitration Agreement) or
              Other Terms, whether arising before, on, or after you enter into these Terms and
              Conditions (any such dispute, claim, or controversy, “Dispute”), shall, if not
              resolved through the pre-arbitration procedures set forth in subsection 4 below, be
              exclusively resolved by individual, binding arbitration in accordance with this
              Arbitration Agreement. You and Moda agree that the arbitrator, and not any federal,
              state, or local court or agency, shall have exclusive authority to resolve any
              Disputes relating to the interpretation, applicability, enforceability, or formation
              of this Arbitration Agreement, including any Dispute that all or any part of this
              Arbitration Agreement is void or voidable, and further, that the arbitrator shall also
              be responsible for determining all threshold arbitrability issues, including issues
              relating to whether these Terms and Conditions are unconscionable or illusory, in
              whole or in part, and any defense to arbitration, including waiver, delay, laches, or
              estoppel. As used in this Arbitration Agreement, you and we may also be referred to as
              the “parties”, or individually, a “party”.
            </Text>
            <Text treatment="body2" family="serif">
              To the fullest extent permissible by applicable law, all Disputes against a Moda Party
              must be filed within one year after such Disputes or cause of action arose or it will
              be forever barred.
            </Text>
            <Text treatment="body2" family="serif">
              If you or Moda files or causes to be filed in court a complaint alleging a Dispute
              that is subject to arbitration under this Arbitration Agreement, the
              defendant/respondent will notify the party or the party's attorney (if an attorney has
              entered an appearance) of the existence of this Arbitration Agreement, and request
              that the complaint be withdrawn. If the party does not withdraw the action within 10
              calendar days of receipt of that notice, the defendant/respondent shall be entitled to
              its costs and fees (including reasonable attorneys' fees) incurred in seeking to
              enforce this Arbitration Agreement.
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">
                Certain Exceptions to Mandatory Arbitration.
              </span>{' '}
              Notwithstanding anything to the contrary in this Arbitration Agreement: You and Moda
              each retain the right to seek injunctive or other equitable relief in a court of
              competent jurisdiction to prevent the actual or threatened infringement,
              misappropriation or violation of a party's copyrights, trademarks, trade secrets,
              patents, or other intellectual property rights, whether registered or unregistered,
              under the laws of any jurisdiction (“IP Claims”); any legal action by Moda against a
              non-consumer or its interactions with governmental and regulatory authorities shall
              not be subject to arbitration (“Non-Consumer Claims”); and either party may also elect
              to have eligible Disputes heard in small claims court seeking only individualized
              relief, so long as the action is not removed or appealed to a court of general
              jurisdiction (together with IP Claims and Non-Consumer Claims, “Excepted Claims”).
            </Text>

            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">
                Governing Law and Jurisdiction for Non-Arbitral Disputes.
              </span>{' '}
              If (i) you timely provide Moda with a valid Opt-Out Notice (as defined below), and you
              are not bound to any previous or other arbitration agreements with Moda; ; or (ii) any
              Dispute is determined not to be subject to arbitration or resolution (including if any
              court of competent jurisdiction or arbitrator, after exhaustion of all appeals,
              determines that the Class Action/Jury Trial Waiver, below, is void or unenforceable
              for any reason, or that your Dispute can proceed on a class, collective,
              representative, or consolidated basis other than the Mass Filing Procedures, below);
              then you and Moda each irrevocably agree that the exclusive jurisdiction and venue
              with respect to such Dispute shall be the federal or state courts of competent
              jurisdiction in New Castle County, Delaware, and any such Dispute and these Terms and
              Conditions shall be governed by and construed in accordance with the substantive and
              procedural laws of the state of the State of Delaware, without regard to choice or
              conflict of law principles.
            </Text>
            <Text treatment="body2" family="serif">
              2. Class Action / Jury Trial Waiver
            </Text>
            <Text treatment="body2" family="serif">
              Except for the permitted Mass Filing Procedures, any arbitration will be conducted
              only on an individual basis and not in a class, collective, representative, or
              consolidated proceeding. You and Moda agree that, to the fullest extent permitted by
              law, each party is waiving the right to a trial by jury or to participate as a
              plaintiff, claimant, or class member in any class, collective, private attorney
              general, representative, or consolidated proceeding in respect of any Dispute, except
              solely as permitted by Mass Filing Procedures, below. This means that you and Moda may
              not (i) bring a Dispute on behalf of a class or group, or bring a Dispute on behalf of
              any other person unless doing so as a parent, guardian, or ward of a minor or in
              another similar capacity for an individual who cannot otherwise bring their own
              individual Dispute, or (ii) participate in any class, collective, private attorney
              general, representative, or consolidated proceeding (other than the permitted Mass
              Filing Procedures) brought by any third party. You or Moda may, however, participate
              in a class-wide settlement.
            </Text>
            <Text treatment="body2" family="serif">
              3. Opt-Out Procedures
            </Text>
            <Text treatment="body2" family="serif">
              You may opt out of this Arbitration Agreement. To do so, you must email
              adr_notices@modaoperandi.com (“Notice Address”) a notice (“Opt-Out Notice”) no later
              than 30 days after you first agreed to this Arbitration Agreement, notwithstanding any
              subsequent amendment to it (“Opt-Out Period”). The Opt-Out Notice must contain your
              full legal name, your complete mailing and email address, and phone number, a clear
              and unconditional statement that you wish to opt out of this Arbitration Agreement,
              and your signature. If your Opt-Out Period has passed, you are not eligible to opt out
              of this Arbitration Agreement, and you will be bound to the terms and conditions of
              this Arbitration Agreement.
            </Text>
            <Text treatment="body2" family="serif">
              If you opt out of this Arbitration Agreement you may still be bound to previous
              versions of this Arbitration Agreement or other arbitration agreements by reason of
              your separate agreement to them, including subsequent agreements to mandatory
              arbitration. In other words, opting out of this Arbitration Agreement shall have no
              effect on any previous or subsequent arbitration agreement you enter(ed) into with
              Moda. In addition, if you do opt out of this Arbitration Agreement, all other
              provisions of the Terms and Conditions (including the Governing Law and Jurisdiction
              for Non-Arbitral Disputes section, above) will continue to be effective
            </Text>
            <Text treatment="body2" family="serif">
              If Moda amends this Arbitration Agreement (other than a change to the Notice Address
              or other non-material changes), they will be effective and binding if and when you
              agree to them by agreeing to the Terms and Conditions. Moda may also provide you with
              notice, email being sufficient, and in such case, the amended Arbitration Agreement
              will be effective no earlier than the date specified in such notice (which will be at
              least 30 days therefrom), unless you reject any such change by sending an email to
              Moda at adr_notices@modaoperandi.com within 30 days of the notice, and by providing:
              (i) your full legal name, (ii) your complete mailing address, (iii) your phone number,
              (iv) the change(s) you are rejecting, (v) and email address associated with any
              purchase from Moda.{' '}
              <span className="TermsAndConditionsPage__bold">
                This is not an opt out of arbitration altogether.
              </span>{' '}
              In addition, your continued or subsequent agreement to these Terms and Conditions is
              acknowledgment of, and agreement to, the changes to the Arbitration Agreement (subject
              to a 30-day Opt-Out Period for those changes), notwithstanding any prior rejection to
              changes that you sent to us under this paragraph.
            </Text>
            <Text treatment="body2" family="serif">
              3. Rules and Procedures
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">Pre-Arbitration Procedures.</span> In
              the event of a Dispute (other than an Excepted Dispute), you and Moda each agree to
              send the other party a written notice of Dispute. A notice of Dispute (“Notice of
              Dispute”) from you to Moda must be emailed to the Notice Address. Any Notice of
              Dispute must include (i) the claimant's full legal name, complete mailing address, and
              email address; (ii) a description of the nature and basis of the Dispute; (iii) any
              relevant facts regarding claimant's use of the Services or Products, including whether
              claimant receives any emails associated with the Services, whether claimant has made a
              purchase from Moda or on the Site, and if so, the date(s) of the purchase(s); and (iv)
              a personally signed statement from the claimant (and not their counsel) verifying the
              accuracy of the contents of the Notice. The Notice of Dispute must be individualized,
              meaning it can concern only your dispute and no other person's dispute. Moda will send
              any Notice of Dispute to you at the email address or mailing address it has for you,
              if any. After receipt of a Notice of Dispute, the parties shall, for a period of 60
              days, engage in a good faith effort to resolve the Dispute. Any applicable limitations
              period (including statute of limitations) shall be tolled while the parties engage in
              the informal dispute resolution procedures set forth in this subsection. All of the
              Pre-Arbitration Procedures are essential so that you and Moda have a meaningful
              opportunity to resolve disputes informally.
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">Arbitration Procedures.</span>If the
              parties cannot resolve the Dispute through the Pre-Arbitration Procedures above, you
              and Moda each agree that all Disputes shall be resolved exclusively through final and
              binding individual arbitration, rather than in court.
            </Text>
            <Text treatment="body2" family="serif">
              All Disputes shall be submitted to National Arbitration and Mediation (“NAM”) for
              arbitration before one arbitrator. The arbitration will be administered by NAM in
              accordance with NAM's Comprehensive Dispute Resolution Rules and Procedures and, as
              applicable, Mass Filing Supplemental Dispute Resolution Rules and Procedures, then in
              effect (“NAM Rules”), except as modified by this Arbitration Agreement. A party who
              desires to initiate arbitration must provide the other party with a written demand for
              arbitration as specified in the NAM Rules. As a requirement for the Dispute to be
              deemed properly filed, you and we agree that the party initiating arbitration must
              submit a certification that they have complied with the Pre-Arbitration Procedures and
              that they are a party to the Arbitration Agreement, a copy of which shall be enclosed
              with or attached to the demand for arbitration. The demand for arbitration and
              certification must be personally signed by the party initiating arbitration (and their
              counsel, if any). A form for initiating arbitration proceedings is available on NAM’s
              website at{' '}
              <a
                className="TermsAndConditionsPage__link"
                href="https://www.namadr.com/resources/rules-fees-forms/"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.namadr.com/resources/rules-fees-forms/
              </a>{' '}
              and for additional information on how to commence an arbitration proceeding, you can
              contact NAM at{' '}
              <a className="TermsAndConditionsPage__link" href="mailto:customerservice@namadr.com">
                customerservice@namadr.com
              </a>
              .
            </Text>
            <Text treatment="body2" family="serif">
              Notwithstanding any choice of law or other provision in these Terms and Conditions,
              the parties agree and acknowledge that this Arbitration Agreement evidences a
              transaction involving interstate commerce and that the Federal Arbitration Act, 9
              U.S.C. § 1 et seq. (“FAA”) will govern its interpretation and enforcement and any
              proceedings under it. It is the intent of the parties that the FAA and the NAM Rules
              shall preempt all state laws to the fullest extent permitted by law. The laws of the
              state of Delaware, without regard to choice or conflict of law principles, shall
              govern (except as to California residents, in which case California law shall govern):
              (i) any issue regarding the interpretation or enforcement of this Arbitration
              Agreement to which the FAA and NAM Rules are found not to apply, and (ii) any other
              issue in a Dispute arbitrated hereunder.
            </Text>
            <Text treatment="body2" family="serif">
              Any arbitration hearing will be conducted remotely by telephone or video conference to
              the extent practicable, but if the arbitrator determines, or the parties agree, that a
              hearing should be conducted in person, the arbitration hearing will take place as
              close to your residence as practicable, or another agreed upon locale. At the
              conclusion of the arbitration proceeding, the arbitrator shall issue a reasoned
              written decision sufficient to explain the essential findings and conclusions on which
              the award is based. An arbitrator's award that has been fully satisfied shall not be
              entered in any court.
            </Text>
            <Text treatment="body2" family="serif">
              As in court, you and Moda agree that any counsel representing a party in arbitration
              shall certify when initiating and proceeding in arbitration that they are complying
              with the requirements of Federal Rule of Civil Procedure 11(b) and any applicable
              state laws of similar import, including certification that the Dispute or relief
              sought is neither frivolous nor brought for an improper purpose. The arbitrator is
              authorized to impose any sanctions under the NAM Rules, Federal Rule of Civil
              Procedure 11, or applicable federal or state law, against all appropriately
              represented parties and counsel.
            </Text>
            <Text treatment="body2" family="serif">
              Except as expressly provided in the Arbitration Agreement, the arbitrator may grant
              any remedy, relief, or outcome that the parties could have received in court,
              including awards of attorneys' fees and costs, in accordance with applicable law. Moda
              reserves all rights and defenses as to each and any Dispute, demand for Arbitration,
              and claimant.
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">Mass Filing Procedures.</span>If NAM
              determines that 25 or more substantially similar arbitration demands presented by or
              with the assistance, coordination, or cooperation of the same law firm, group of law
              firms, cooperating law firms, or organization(s) are allowed to be submitted for
              arbitration, NAM's mass filing fee structure shall apply and the parties agree that
              the arbitrations will proceed in accordance with the batching process as follows: (i)
              NAM shall administer the arbitration demands in batches of at least 25 demands for
              arbitration of a substantially similar nature, with the discretion to create
              additional batches if NAM finds that they are necessary to facilitate the efficient
              resolution of demands; (ii) NAM shall provide for the concurrent resolution of each
              batch as a single consolidated arbitration with one arbitrator, one procedural
              calendar, one hearing (if any), and one final award; and (iii) following such
              determination of a mass filing, NAM shall apply a single set of admin and panel prep
              fees per batch in accordance with NAM's fee schedule. All parties agree that
              arbitrations are of a “substantially similar nature” for purposes of these Mass Filing
              Procedures if they arise out of or relate to the same or similar event or factual
              scenario and raise the same or similar legal issues and seek the same or similar
              relief. These Mass Filing Procedures shall in no way be interpreted as authorizing
              class arbitrations of any kind.
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">Arbitrator's Fees and Costs.</span>
              You are responsible for all of your own costs (including any attorneys' fees) and any
              costs or fees as set forth in the applicable NAM fee schedule. If you do not timely
              pay any required fees to NAM, any refusal by Moda to pay such fees on your behalf
              shall not void or otherwise invalidate this Arbitration Agreement, which shall remain
              in full force and effect.
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">Confidentiality.</span>
              The parties agree that the arbitrator is authorized to issue an order requiring that
              confidential information of either party disclosed during the arbitration (whether in
              documents or orally) may not be used or disclosed except in connection with the
              arbitration or a proceeding to enforce the arbitration award and that any permitted
              court filing of confidential information must be done under seal to the furthest
              extent permitted by law.
            </Text>
            <Text treatment="body2" family="serif">
              <span className="TermsAndConditionsPage__italic">
                Requirement of Individualized Relief.
              </span>
              The parties agree that, to the fullest extent permitted by law, the arbitrator is
              authorized, upon either party's request, to award declaratory or injunctive relief
              only in favor of the individual party seeking relief and only to the extent necessary
              to provide relief warranted by that party's individual Dispute.
            </Text>
            <Text treatment="body2" family="serif">
              5. Severability & Survival
            </Text>
            <Text treatment="body2" family="serif">
              If any provision or portion of this Arbitration Agreement is found to be void,
              invalid, or otherwise unenforceable, then that provision or portion shall be deemed to
              be severable and, if possible, superseded by a valid, enforceable provision, or
              portion thereof, that matches the intent of the original provision, or portion
              thereof, as closely as possible, and further, the remainder of this Arbitration
              Agreement shall continue to be enforceable and valid according to its terms, and you
              may still be bound to previous versions of this Arbitration Agreement by reason of
              your separate agreement to those previous versions.
            </Text>
            <Text treatment="body2" family="serif">
              This Arbitration Agreement shall survive termination of these Terms and Conditions.
              Except as provided in subsections A(1) and A(3) above, the terms and conditions of
              this Arbitration Agreement shall supersede and replace any and all previous
              arbitration, dispute resolution, and/or class action/jury waiver agreements you may
              have entered into with Moda.
            </Text>
            <Text treatment="body1">
              B.{' '}
              <Text className="TermsAndConditionsPage__underline">
                DISPUTE RESOLUTION FOR NON-U.S. RESIDENTS
              </Text>
            </Text>
            <Text treatment="body1">
              PLEASE READ THIS SECTION CAREFULLY. IT DESCRIBES HOW DISPUTES BETWEEN YOU AND MODA ARE
              RESOLVED. NOTHING IN THESE TERMS AND CONDITIONS EXCLUDES LOCAL CONSUMER LAWS WHICH
              APPLY TO YOU AND WHICH CANNOT LAWFULLY BE EXCLUDED (INCLUDING TO THE EXTENT RELEVANT,
              THE RIGHT TO BRING ACTION IN THE LOCAL COURTS OF YOUR COUNTRY OF RESIDENCE).
            </Text>
            <Text treatment="body2" family="serif">
              To the fullest extent permitted by applicable law, any dispute, claim, or controversy
              between you, on the one hand, and Moda and/or any of its affiliates and/or its and
              their employees, officers, or directors (each, a “Moda Party”), on the other hand,
              including, but not limited to, those relating to or arising from (i) the Services,
              Products, or (ii) these Terms and Conditions, or any other contract, arrangement, Moda
              policy or FAQ, agreement, or terms entered into by you and Moda that are governed by,
              or otherwise incorporate or reference, or are incorporated by reference in, these
              Terms and Conditions (“Other Terms”), including, without limitation, those relating to
              the formation, breach, termination, enforcement, interpretation, validity, scope, or
              applicability of the Terms and Conditions or Other Terms, whether arising before, on,
              or after you enter into these Terms and Conditions (any such dispute, claim, or
              controversy, “Dispute”), shall be governed by and construed in accordance with the
              laws of the State of Delaware, United States, without regard to its conflict of laws
              principles.
            </Text>
            <Text treatment="body2" family="serif">
              To the fullest extent permitted by applicable law, these Terms and Conditions and any
              Dispute shall be governed by and construed in accordance with the laws of the State of
              Delaware, without regard to its conflict of laws principles, and you and we
              irrevocably consent to the exclusive jurisdiction and venue of the state and federal
              courts located in New Castle County, Delaware for the resolution of any such Disputes,
              and further, waive any objection to the laying of venue in such courts and agrees that
              shall not be a basis for transfer or dismissal.
            </Text>
            <Text treatment="body2" family="serif">
              WHERE PERMITTED BY APPLICABLE LAW, YOU AND MODA AGREE THAT EACH MAY BRING CLAIMS
              AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR
              CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION. This limitation on class
              actions will not apply where prohibited by the laws of your country of residence.
            </Text>
            <Text treatment="body2" family="serif">
              To the fullest extent permissible by applicable law, any dispute against a Moda Party
              must be filed within one year after such dispute or cause of action arose, or it will
              be forever barred.
            </Text>
          </Stack>
        </div>
      </div>
    </>
  );
};
